/* Section-level custom elements. Each one is inert unless its section renders.
   Kept apart from theme.js so neither file approaches Shopify's 10KB per-file budget. */
(() => {
  'use strict';

  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Product media ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.items = $$('.product__media-item', this);
      this.thumbs = $$('[data-media-target]', this);
      if (!this.items.length) return;

      this.thumbs.forEach((thumb) => on(thumb, 'click', () => this.show(thumb.dataset.mediaTarget)));

      // Follow the variant picker, which is what a shopper expects from a swatch.
      const map = $('[data-variant-media]', this);
      if (map) {
        try {
          this.variantMedia = JSON.parse(map.textContent);
        } catch {
          this.variantMedia = {};
        }
        const form = $('.product-form');
        if (form) {
          on(form, 'change', () => {
            const id = $('[data-variant-input]', form)?.value;
            const mediaId = this.variantMedia[id];
            if (mediaId) this.show(String(mediaId));
          });
        }
      }
    }

    show(mediaId) {
      let matched = false;
      this.items.forEach((item) => {
        const active = item.dataset.mediaId === mediaId;
        if (active) matched = true;
        // A lazy image that was hidden has not been fetched yet, so the frame showed blank
        // for a moment when a variant revealed it. Ask for it now.
        if (active) $('img[loading="lazy"]', item)?.setAttribute('loading', 'eager');
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        // Stop a video that scrolls out of view rather than leaving it playing.
        if (!active) $('video, iframe', item)?.pause?.();
      });
      if (!matched) return;
      this.thumbs.forEach((thumb) => {
        if (thumb.dataset.mediaTarget === mediaId) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      const items = $$('.announcement-bar__item', this);
      if (items.length < 2 || this.dataset.autorotate !== 'true' || reducedMotion()) return;
      let i = 0;
      this.timer = setInterval(() => {
        items[i].hidden = true;
        i = (i + 1) % items.length;
        items[i].hidden = false;
      }, Number(this.dataset.speed || 5) * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Slideshow ---------- */
  class SlideshowCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-slide]', this);
      if (!this.slides.length) return;
      this.dots = $$('[data-dot]', this);
      this.index = 0;
      this.show(0);

      on($('[data-prev]', this), 'click', () => this.show(this.index - 1));
      on($('[data-next]', this), 'click', () => this.show(this.index + 1));
      this.dots.forEach((dot) => on(dot, 'click', () => this.show(Number(dot.dataset.dot))));

      if (this.dataset.autoplay === 'true' && this.slides.length > 1 && !reducedMotion()) {
        this.timer = setInterval(() => this.show(this.index + 1), Number(this.dataset.speed || 6) * 1000);
        on(this, 'pointerenter', () => clearInterval(this.timer));
        on(this, 'focusin', () => clearInterval(this.timer));
      }
    }

    show(next) {
      const total = this.slides.length;
      this.index = ((next % total) + total) % total;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('aria-hidden', !active);
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === this.index)));
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('slideshow-carousel', SlideshowCarousel);

  /* ---------- Countdown ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const target = new Date(String(this.dataset.deadline).replace(' ', 'T')).getTime();
      if (Number.isNaN(target)) return;
      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          this.textContent = this.dataset.expiredText || '';
          clearInterval(this.timer);
          return;
        }
        const s = Math.floor(diff / 1000);
        this.set('days', Math.floor(s / 86400));
        this.set('hours', Math.floor((s % 86400) / 3600));
        this.set('minutes', Math.floor((s % 3600) / 60));
        this.set('seconds', s % 60);
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }

    set(unit, value) {
      const el = $(`[data-${unit}]`, this);
      if (el) el.textContent = String(value).padStart(2, '0');
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Deferred video ---------- */
  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      on($('button', this), 'click', () => {
        const src = this.dataset.src;
        if (!src) return;
        const iframe = document.createElement('iframe');
        iframe.src = src.includes('?') ? `${src}&autoplay=1` : `${src}?autoplay=1`;
        iframe.title = 'Video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.loading = 'lazy';
        this.replaceChildren(iframe);
      });
    }
  }
  customElements.define('deferred-video', DeferredVideo);

  /* ---------- Recently viewed ---------- */
  const RECENT_KEY = 'recently-viewed';

  const readRecent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch {
      return [];
    }
  };

  const writeRecent = (list) => {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
    } catch {
      // Private browsing and blocked storage are normal, not errors.
    }
  };

  const currentHandle = $('.main-product-section')
    ? window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1]
    : null;

  if (currentHandle) {
    writeRecent([currentHandle, ...readRecent().filter((h) => h !== currentHandle)]);
  }

  const recentHolder = $('[data-recently-viewed]');
  if (recentHolder) {
    const grid = $('[data-recently-viewed-grid]', recentHolder);
    const limit = Number(recentHolder.dataset.limit || 4);
    const handles = readRecent().filter((h) => h !== currentHandle).slice(0, limit);

    on($('[data-clear-recently-viewed]', recentHolder), 'click', () => {
      writeRecent([]);
      recentHolder.hidden = true;
    });

    if (handles.length) {
      // Each card is fetched from its own product page, so markup stays in Liquid.
      Promise.all(
        handles.map((handle) =>
          fetch(`/products/${handle}?view=card`)
            .then((res) => (res.ok ? res.text() : null))
            .catch(() => null)
        )
      ).then((pages) => {
        const cards = pages
          .filter(Boolean)
          .map((html) => $('.product-card', new DOMParser().parseFromString(html, 'text/html')))
          .filter(Boolean);

        if (!cards.length) return;
        cards.forEach((card) => {
          const item = document.createElement('li');
          item.className = 'grid__item';
          item.appendChild(card);
          grid.appendChild(item);
        });
        recentHolder.hidden = false;
      });
    }
  }

  /* ---------- Sticky buy bar ---------- */
  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      this.form = $('.product-form');
      this.anchor = this.form && $('[name="add"]', this.form);
      if (!this.anchor) return;

      this.button = $('[data-sticky-add]', this);
      on(this.button, 'click', () => {
        // Mirror the real form rather than owning a second one, so variant state
        // and validation stay in one place.
        this.anchor.click();
      });

      // Show the bar only once the real button has left the viewport.
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.hidden = entry.isIntersecting;
        },
        { rootMargin: '0px 0px -20px 0px' }
      );
      this.observer.observe(this.anchor);

      on(this.form, 'change', () => this.sync());
    }

    sync() {
      if (!this.button || !this.anchor) return;
      this.button.disabled = this.anchor.disabled;
      const price = $('.price', this.form.closest('.product__info') || document);
      const target = $('[data-sticky-price]', this);
      if (price && target) target.innerHTML = price.innerHTML;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  customElements.define('sticky-buy-bar', StickyBuyBar);
})();
